import { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getCompanyIdForUser, findAccountIdByEmail } from '../services/firestoreService';
import { isQuickBooksConnected } from '../services/quickbooksService';
import {
  isJobTreadConnected,
  getJobTreadOrganizations,
  getJobTreadLeads,
  getJobTreadCustomerLeads,
  computeCloseRatioFromLeads,
  resolveJobTreadOrg
} from '../services/jobtreadService';
import { resolveJobTreadFieldMappings, loadJobTreadLeadPreferences, normalizeLeadPreferences } from '../services/jobtreadFieldMapping';
import { isClosedStatus } from '../services/jobtreadConstants';
import { fetchAccountWebsiteAnalytics } from '../services/websiteAnalyticsService';
import { getUserAudits, transformAuditResults } from '../services/websiteAuditService';
import { getUserSocialMediaData } from '../services/socialMediaService';
import { getUserNewsletterData } from '../services/newsletterService';
import { getReviewsData } from '../services/reviewsService';
import { getMetricStatus, getCoachingCue } from '../components/marketing/marketingBenchmarks';
import { makeTrace, recordFetch, fmtN } from '../utils/metricTrace';
import { SALES_TIMEFRAMES, salesTimeframeLabel, buildTimeframeRange } from './useSalesDashboard';

// Same timeframe options as the sales analytics tab so both windows match.
export const MARKETING_TIMEFRAMES = SALES_TIMEFRAMES;
export const marketingTimeframeLabel = salesTimeframeLabel;

const EMPTY_METRIC = { value: '—', rawValue: null, status: 'none', source: 'none', series: [], sub: null, cue: null, trace: null };

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** Truthy test for a JobTread boolean custom-field value. */
const isTruthy = (v) => {
  const s = String(v ?? '').trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1' || s === 'y' || s === 'checked' || s === 'on';
};

const fmtCurrency = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`);
const fmtPercent = (v, digits = 1) =>
  v == null || Number.isNaN(v) ? '—' : `${(Math.round(v * Math.pow(10, digits)) / Math.pow(10, digits)).toFixed(digits)}%`;

const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/** Aggregate social media totals across platforms. */
const aggregateSocial = (socialData) => {
  const platforms = ['facebook', 'instagram', 'tiktok', 'youtube'];
  let totalFollowers = 0;
  let totalLeads = 0;
  let engagementSum = 0;
  let engagementCount = 0;
  const perPlatform = {};

  platforms.forEach((p) => {
    const d = socialData?.socialData?.[p] || socialData?.[p] || {};
    perPlatform[p] = { followers: num(d.followers), leads: num(d.leads), engagement: num(d.engagement) };
    totalFollowers += num(d.followers);
    totalLeads += num(d.leads);
    if (num(d.engagement) > 0) {
      engagementSum += num(d.engagement);
      engagementCount += 1;
    }
  });

  return {
    totalFollowers,
    totalLeads,
    avgEngagement: engagementCount > 0 ? engagementSum / engagementCount : 0,
    perPlatform
  };
};

/** Aggregate paid media campaigns. */
const aggregateCampaigns = (campaigns) => {
  const totals = campaigns.reduce(
    (acc, c) => {
      const m = c.metrics || {};
      acc.spent += num(c.spent);
      acc.revenue += num(m.revenue);
      acc.conversions += num(m.conversions);
      acc.impressions += num(m.impressions);
      acc.clicks += num(m.clicks);
      return acc;
    },
    { spent: 0, revenue: 0, conversions: 0, impressions: 0, clicks: 0 }
  );
  totals.cpl = totals.conversions > 0 ? totals.spent / totals.conversions : null;
  totals.cpc = totals.clicks > 0 ? totals.spent / totals.clicks : null;
  totals.roas = totals.spent > 0 ? ((totals.revenue - totals.spent) / totals.spent) * 100 : null;
  return totals;
};

/** Aggregate newsletter campaign metrics. */
const aggregateNewsletter = (campaigns) => {
  const list = campaigns || [];
  if (!list.length) return { openRate: null, subscribers: 0, clickRate: null };

  let openSum = 0;
  let clickSum = 0;
  let count = 0;
  let subscribers = 0;

  list.forEach((c) => {
    const m = c.metrics || {};
    if (m.openRate != null) {
      openSum += num(m.openRate);
      count += 1;
    }
    if (m.clickRate != null) clickSum += num(m.clickRate);
    subscribers = Math.max(subscribers, num(c.subscribers || c.totalEmails));
  });

  return {
    openRate: count > 0 ? openSum / count : null,
    clickRate: count > 0 ? clickSum / count : null,
    subscribers
  };
};

// userId/accountId let an internal viewer (CRM Portal View) load the
// dashboard for a specific client instead of the logged-in user.
export const useMarketingDashboard = (timeframe = 'ytd', { userId = null, accountId = null } = {}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reloadIndex, setReloadIndex] = useState(0);
  const refresh = () => setReloadIndex((i) => i + 1);
  const [state, setState] = useState({
    companyId: null,
    jobtreadConnected: false,
    jobtreadError: null,
    ga4Connected: false,
    qbConnected: false,
    asOfDate: new Date(),
    debug: null,
    sales: null,
    leadSettings: null,
    qualified: { source: 'none', pct: null, qualifiedCount: 0, answered: 0, unanswered: 0, total: 0 },
    leadsActual: null,
    metrics: {
      leads: EMPTY_METRIC,
      closeRatio: EMPTY_METRIC,
      costPerLead: EMPTY_METRIC,
      website: EMPTY_METRIC,
      social: EMPTY_METRIC,
      paidMedia: EMPTY_METRIC,
      newsletter: EMPTY_METRIC,
      reviews: EMPTY_METRIC,
      leadSources: EMPTY_METRIC
    }
  });

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      const uid = userId || currentUser.uid;
      const today = new Date();
      const currentYear = today.getFullYear();
      const tf = buildTimeframeRange(timeframe, today);
      const tfLabel = salesTimeframeLabel(timeframe);
      const inTimeframe = (dateStr) => {
        const d = dateStr ? new Date(dateStr) : null;
        return !!d && !Number.isNaN(d.getTime()) && d >= tf.start && d < tf.end;
      };
      const monthPos = new Map(tf.months.map((mo, i) => [`${mo.year}-${mo.monthIndex}`, i]));
      const fetches = [];

      let companyId = accountId || null;
      if (!companyId) {
        try {
          companyId = await getCompanyIdForUser(uid);
          // Email fallback only applies to the logged-in user — currentUser.email
          // is the wrong email for an overridden userId.
          if (!companyId && !userId && currentUser.email) {
            companyId = await findAccountIdByEmail(currentUser.email, 'Clients').catch(() => null);
          }
        } catch (_) {
          companyId = null;
        }
      }

      // JobTread grants live server-side; queries pass targetUserId and the
      // server resolves that user's grant (the caller's own when uid is theirs).
      const jobtreadConnected = await isJobTreadConnected(uid).catch(() => false);
      const qbConnected = await isQuickBooksConnected(companyId).catch(() => false);

      const [userDocRes, ga4Res, socialRes, campaignsRes, newsletterRes, reviewsRes, auditsRes] =
        await Promise.allSettled([
          getDoc(doc(db, 'users', uid)),
          fetchAccountWebsiteAnalytics({ startDate: '30daysAgo', endDate: 'today' }),
          getUserSocialMediaData(uid),
          getDocs(query(collection(db, 'campaigns'), where('userId', '==', uid))),
          getUserNewsletterData(uid),
          getReviewsData(uid),
          getUserAudits(uid)
        ]);

      const userDoc = userDocRes.status === 'fulfilled' && userDocRes.value.exists()
        ? userDocRes.value.data()
        : null;
      fetches.push({ key: 'manual.userDoc', fn: 'users/{uid} doc', params: { uid }, status: userDocRes.status, error: userDocRes.status === 'rejected' ? String(userDocRes.reason?.message || userDocRes.reason) : null });

      const marketingLeads = userDoc?.marketingLeads || [];
      const leadsROI = userDoc?.leadsROI || null;
      // Manual channel totals set right on the analytics cards ("Update").
      const channelTotals = userDoc?.marketingChannelTotals || {};

      const ga4Data = recordFetch(fetches, 'ga4.analytics', 'fetchAccountWebsiteAnalytics', { startDate: '30daysAgo', endDate: 'today' }, ga4Res);
      const ga4Connected = ga4Data?.ok === true;

      const socialRaw = recordFetch(fetches, 'manual.social', 'getUserSocialMediaData', { uid }, socialRes);
      const socialAgg = aggregateSocial(socialRaw || {});

      const campaigns = [];
      if (campaignsRes.status === 'fulfilled') {
        campaignsRes.value.forEach((d) => campaigns.push({ id: d.id, ...d.data() }));
      }
      fetches.push({ key: 'manual.campaigns', fn: 'campaigns query', params: { uid }, status: campaignsRes.status, error: campaignsRes.status === 'rejected' ? String(campaignsRes.reason?.message || campaignsRes.reason) : null });
      const campaignAgg = aggregateCampaigns(campaigns);

      const newsletterRaw = recordFetch(fetches, 'manual.newsletter', 'getUserNewsletterData', { uid }, newsletterRes);
      const newsletterAgg = aggregateNewsletter(newsletterRaw?.campaigns);

      const reviewsRaw = recordFetch(fetches, 'manual.reviews', 'getReviewsData', { uid }, reviewsRes);
      const reviewsData = reviewsRaw?.success ? reviewsRaw.data : null;

      let latestSeoScore = null;
      if (auditsRes.status === 'fulfilled' && auditsRes.value?.success && auditsRes.value.audits?.length) {
        const latest = auditsRes.value.audits.find((a) => a.status === 'completed');
        if (latest?.results) {
          const transformed = transformAuditResults(latest.results);
          latestSeoScore = transformed?.seoScore ?? null;
        }
      }
      fetches.push({ key: 'manual.audits', fn: 'getUserAudits', params: { uid }, status: auditsRes.status, error: auditsRes.status === 'rejected' ? String(auditsRes.reason?.message || auditsRes.reason) : null });

      /* -------- JobTread leads -------- */
      let jtLeads = null;
      let jtLeadsDiagnostics = null;
      let jtOrg = { orgId: null, orgName: null, orgCount: 0, multipleOrgs: false };
      let jtMapping = null;
      let jtError = null;
      if (jobtreadConnected) {
        // Every JobTread query runs under the target user's grant — passed
        // explicitly per call so overlapping runs can't poison each other.
        try {
          const orgsData = await getJobTreadOrganizations({ targetUserId: uid });
          jtOrg = resolveJobTreadOrg(orgsData);
          if (!jtOrg.orgId) {
            jtError = 'The JobTread grant key works but belongs to no organization.';
          }
          if (jtOrg.orgId) {
            const { mapping } = await resolveJobTreadFieldMappings(uid, jtOrg.orgId, { targetUserId: uid });
            jtMapping = mapping;
            const res = await getJobTreadLeads(jtOrg.orgId, { mapping, targetUserId: uid });
            jtLeads = res.leads;
            jtLeadsDiagnostics = res.diagnostics;
            fetches.push({ key: 'jt.leads', fn: 'getJobTreadLeads', params: { orgId: jtOrg.orgId }, status: 'fulfilled', error: res.diagnostics?.error || null });
          }
        } catch (e) {
          jtLeads = null;
          jtError = e?.message || String(e);
          fetches.push({ key: 'jt.leads', fn: 'getJobTreadLeads', params: { orgId: jtOrg.orgId }, status: 'rejected', error: jtError });
        }
        // Surface failures the per-fetch wrappers swallow into diagnostics —
        // otherwise a dead grant (e.g. expired JobTread subscription) renders
        // as an indistinguishable "no data" empty state.
        if (!jtError) {
          jtError = jtLeadsDiagnostics?.error || null;
        }
      }

      const metrics = {};
      const jtWarnings = jtLeadsDiagnostics?.warnings || [];

      /* -------- Lead definition — what this org calls a "lead" --------
         entity 'job'|'customer' (New Job vs New Customer); scope 'all' (every
         one created) or 'status' (only those whose chosen status field is in
         the selected set). The status field is built-in job status ('') or any
         custom field; lead source is the mapped custom field on that entity. */
      const leadPrefs = normalizeLeadPreferences(await loadJobTreadLeadPreferences(uid).catch(() => null));
      const { leadEntity, leadScope, statusFieldId } = leadPrefs;
      const prefStatuses = (leadPrefs.statuses || []).map((s) => String(s).toLowerCase());

      // Base lead set for the chosen entity. Jobs are already fetched; customers
      // are fetched on demand (enriched for their custom fields).
      let baseLeads = [];
      let custDiagnostics = null;
      if (leadEntity === 'customer') {
        if (jobtreadConnected && jtOrg.orgId) {
          const custRes = await getJobTreadCustomerLeads(jtOrg.orgId, { mapping: jtMapping, targetUserId: uid }).catch(() => ({ leads: [], diagnostics: null }));
          baseLeads = custRes.leads || [];
          custDiagnostics = custRes.diagnostics;
          fetches.push({ key: 'jt.customerLeads', fn: 'getJobTreadCustomerLeads', params: { orgId: jtOrg.orgId }, status: 'fulfilled', error: custRes.diagnostics?.error || null });
          if (custRes.diagnostics?.warnings?.length) jtWarnings.push(...custRes.diagnostics.warnings);
        }
      } else {
        baseLeads = jtLeads || [];
      }
      if (!jtError) {
        jtError = custDiagnostics?.error || null;
      }

      // Value of the configured status field for a lead ('' field = built-in).
      const leadStatusValue = (l) =>
        String((statusFieldId ? l.customFields?.[statusFieldId] : l.status) ?? '').trim();

      // Distinct status values per candidate field over the base leads — feeds
      // the lead-settings value checklist. Key '' = built-in status.
      const statusTally = {};
      const bump = (key, val) => {
        const v = String(val ?? '').trim();
        if (!v) return;
        statusTally[key] = statusTally[key] || {};
        statusTally[key][v] = (statusTally[key][v] || 0) + 1;
      };
      baseLeads.forEach((l) => {
        bump('', l.status);
        Object.entries(l.customFields || {}).forEach(([fid, val]) => bump(fid, val));
      });
      const statusValues = Object.fromEntries(
        Object.entries(statusTally).map(([k, m]) => [
          k,
          Object.entries(m).map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count)
        ])
      );

      const entityNoun = leadEntity === 'customer' ? 'customers' : 'jobs';
      let effectiveLeads = baseLeads;
      let leadDefinitionLabel = `${entityNoun} created`;
      if (leadScope === 'status' && prefStatuses.length) {
        effectiveLeads = baseLeads.filter((l) => prefStatuses.includes(leadStatusValue(l).toLowerCase()));
        leadDefinitionLabel = `${entityNoun} in ${prefStatuses.length} selected status${prefStatuses.length === 1 ? '' : 'es'}`;
      }
      const jtLeadsAvailable = jobtreadConnected && Array.isArray(effectiveLeads);

      // LEADS — windowed to the selected timeframe. Benchmarks are annual, so
      // the status pill only applies on the classic YTD view.
      const scopedJtLeads = jtLeadsAvailable
        ? effectiveLeads.filter((l) => inTimeframe(l.createdDate))
        : [];
      if (jtLeadsAvailable && effectiveLeads.length) {
        const series = tf.months.map(({ label }) => ({ label, value: 0 }));
        scopedJtLeads.forEach((l) => {
          const d = new Date(l.createdDate);
          const i = monthPos.get(`${d.getFullYear()}-${d.getMonth()}`);
          if (i != null) series[i].value += 1;
        });
        const total = scopedJtLeads.length;
        const status = timeframe === 'ytd' ? getMetricStatus('leadsYtd', total) : 'none';
        metrics.leads = {
          value: total.toLocaleString(),
          rawValue: total,
          status,
          source: 'jobtread',
          series,
          sub: `${total} ${leadDefinitionLabel} in JobTread · ${tfLabel}`,
          cue: timeframe === 'ytd' ? getCoachingCue('leadsYtd', status) : null,
          trace: makeTrace('leads', {
            source: 'jobtread',
            sourceReason: `JobTread connected; lead = ${leadEntity} (${leadScope}) over org ${jtOrg.orgName || jtOrg.orgId}, windowed to ${tfLabel}`,
            formula: `Leads = count of ${leadDefinitionLabel} in ${tfLabel} = ${total} (of ${effectiveLeads.length} matching, ${baseLeads.length} ${entityNoun} fetched)`,
            inputs: { total, matching: effectiveLeads.length, totalFetched: baseLeads.length, leadEntity, leadScope, statusFieldId, prefStatuses, orgId: jtOrg.orgId, hasMoreJobs: jtLeadsDiagnostics?.hasMoreJobs },
            warnings: jtWarnings
          })
        };
      } else if (marketingLeads.length) {
        const series = tf.months.map(({ year: yy, monthIndex, label }) => ({
          label,
          value: marketingLeads
            .filter((e) => e.year === yy && e.month === MONTH_FULL[monthIndex])
            .reduce((sum, e) => sum + num(e.count), 0)
        }));
        const total = series.reduce((sum, p) => sum + p.value, 0);
        const status = timeframe === 'ytd' ? getMetricStatus('leadsYtd', total) : 'none';
        metrics.leads = {
          value: total.toLocaleString(),
          rawValue: total,
          status,
          source: 'manual',
          series,
          sub: `${tfLabel} · from your entries`,
          cue: timeframe === 'ytd' ? getCoachingCue('leadsYtd', status) : null,
          trace: makeTrace('leads', {
            source: 'manual',
            sourceReason: jobtreadConnected ? 'JobTread returned no jobs — using your monthly lead entries' : 'JobTread not connected — using your monthly lead entries',
            formula: `Leads = Σ marketingLeads counts in ${tfLabel} = ${total}`,
            inputs: { total, entryCount: marketingLeads.length },
            warnings: jtWarnings
          })
        };
      } else {
        metrics.leads = { ...EMPTY_METRIC, trace: makeTrace('leads', { source: 'none', sourceReason: 'No JobTread jobs and no manual lead entries', warnings: jtWarnings }) };
      }

      // LEAD SOURCES — attribution from the JobTread lead-source custom field
      // over the same windowed lead set as the Leads chart.
      if (scopedJtLeads.length) {
        const counts = {};
        let unattributed = 0;
        scopedJtLeads.forEach((l) => {
          const s = String(l.leadSource || '').trim();
          if (s) counts[s] = (counts[s] || 0) + 1;
          else unattributed += 1;
        });
        const sources = Object.entries(counts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
        const attributed = scopedJtLeads.length - unattributed;
        if (sources.length) {
          metrics.leadSources = {
            value: sources.length.toLocaleString(),
            rawValue: sources.length,
            status: 'none',
            source: 'jobtread',
            series: [],
            sources,
            top: sources[0],
            attributed,
            unattributed,
            sub: `${attributed} of ${scopedJtLeads.length} leads attributed · ${tfLabel}`,
            cue: null,
            trace: makeTrace('leadSources', {
              source: 'jobtread',
              sourceReason: `Lead-source custom field (${jtLeadsDiagnostics?.mappingUsed?.leadSource || 'mapped field'}) on JobTread jobs in ${tfLabel}`,
              formula: `Sources = distinct lead-source values over ${scopedJtLeads.length} leads = ${sources.length}; top = ${sources[0].name} (${sources[0].count})`,
              inputs: { attributed, unattributed, distinctSources: sources.length },
              warnings: jtWarnings
            })
          };
        } else {
          metrics.leadSources = {
            ...EMPTY_METRIC,
            trace: makeTrace('leadSources', {
              source: 'none',
              sourceReason: `No ${entityNoun} in this window carry a lead-source value (field unmapped or blank)`,
              warnings: jtWarnings
            })
          };
        }
      } else {
        metrics.leadSources = {
          ...EMPTY_METRIC,
          trace: makeTrace('leadSources', {
            source: 'none',
            sourceReason: jobtreadConnected ? 'No JobTread leads in this window' : 'JobTread not connected — lead-source attribution unavailable',
            warnings: jtWarnings
          })
        };
      }

      // QUALIFIED LEADS — from a mapped yes/no custom field on the leads. Only
      // leads that actually carry an answer (yes OR no) count toward the rate;
      // leads with no value are reported separately as "not marked". pct feeds
      // the Leads Needed calc so it reflects the real qualified rate.
      const qualifiedFieldId = jtMapping?.fields?.qualifiedLead?.fieldId || null;
      let qualified = { source: 'none', pct: null, qualifiedCount: 0, answered: 0, unanswered: 0, total: 0 };
      if (jtLeadsAvailable && qualifiedFieldId && scopedJtLeads.length) {
        const hasAnswer = (l) => {
          const v = l.customFields?.[qualifiedFieldId];
          return v != null && String(v).trim() !== '';
        };
        const answeredLeads = scopedJtLeads.filter(hasAnswer);
        const answered = answeredLeads.length;
        const qc = answeredLeads.filter((l) => isTruthy(l.customFields?.[qualifiedFieldId])).length;
        qualified = {
          source: 'jobtread',
          pct: answered > 0 ? Math.round((qc / answered) * 1000) / 10 : null,
          qualifiedCount: qc,
          answered,
          unanswered: scopedJtLeads.length - answered,
          total: scopedJtLeads.length
        };
      }

      // CURRENT LEADS — actuals for the live calendar year / quarter / month /
      // week (independent of the selected display timeframe), to compare
      // against the Leads Needed targets.
      const qStart = new Date(currentYear, Math.floor(today.getMonth() / 3) * 3, 1);
      const mStart = new Date(currentYear, today.getMonth(), 1);
      const yStart = new Date(currentYear, 0, 1);
      const wStart = new Date(today);
      wStart.setDate(today.getDate() - today.getDay());
      wStart.setHours(0, 0, 0, 0);
      const inRange = (l, start) => {
        const d = l.createdDate ? new Date(l.createdDate) : null;
        return !!d && !Number.isNaN(d.getTime()) && d >= start && d <= today;
      };
      let leadsActual = null;
      if (jtLeadsAvailable && effectiveLeads.length) {
        leadsActual = {
          annual: effectiveLeads.filter((l) => inRange(l, yStart)).length,
          quarter: effectiveLeads.filter((l) => inRange(l, qStart)).length,
          month: effectiveLeads.filter((l) => inRange(l, mStart)).length,
          week: effectiveLeads.filter((l) => inRange(l, wStart)).length
        };
      } else if (marketingLeads.length) {
        const curMonthName = MONTH_FULL[today.getMonth()];
        const qMonths = [0, 1, 2].map((k) => MONTH_FULL[Math.floor(today.getMonth() / 3) * 3 + k]);
        const yearEntries = marketingLeads.filter((e) => e.year === currentYear);
        leadsActual = {
          annual: yearEntries.reduce((s, e) => s + num(e.count), 0),
          quarter: yearEntries.filter((e) => qMonths.includes(e.month)).reduce((s, e) => s + num(e.count), 0),
          month: yearEntries.filter((e) => e.month === curMonthName).reduce((s, e) => s + num(e.count), 0),
          week: null // manual entries are monthly — no weekly granularity
        };
      }

      // CLOSE RATIO
      if (jtLeads?.length) {
        const { closeRatio, closedCount, totalCount } = computeCloseRatioFromLeads(jtLeads);
        const status = getMetricStatus('closeRatio', closeRatio);
        metrics.closeRatio = {
          value: fmtPercent(closeRatio),
          rawValue: closeRatio,
          status,
          source: 'jobtread',
          series: [],
          sub: `${closedCount} closed of ${totalCount} leads`,
          cue: getCoachingCue('closeRatio', status),
          trace: makeTrace('closeRatio', {
            source: 'jobtread',
            sourceReason: `Computed from the same JobTread jobs as the Leads widget (org ${jtOrg.orgName || jtOrg.orgId})`,
            formula: `Close ratio = closed ${closedCount} / total ${totalCount} × 100 = ${fmtPercent(closeRatio)} (closed = closedOn set OR closed-status vocabulary)`,
            inputs: { closedCount, totalCount, jobsWithStatus: jtLeadsDiagnostics?.jobsWithStatus, jobsWithClosedOn: jtLeadsDiagnostics?.jobsWithClosedOn },
            warnings: jtWarnings
          })
        };
      } else {
        metrics.closeRatio = { ...EMPTY_METRIC, trace: makeTrace('closeRatio', { source: 'none', sourceReason: jobtreadConnected ? 'JobTread returned no jobs' : 'JobTread not connected', warnings: jtWarnings }) };
      }

      // SALES-DERIVED CALCULATOR INPUTS — closed revenue, close ratio, and
      // average job size from JobTread jobs; feeds the embedded Leads
      // Calculator on the Analytics tab. Only claim revenue/avg job size when
      // contract values are actually present (an unmapped value field
      // previously produced $0).
      const jtHasValues = (jtLeadsDiagnostics?.jobsWithContractValue ?? 0) > 0;
      let sales = null;
      if (jtLeads?.length) {
        const { closeRatio, totalCount } = computeCloseRatioFromLeads(jtLeads);
        const closedValues = jtHasValues
          ? jtLeads
              .filter((l) => !!l.closedOn || isClosedStatus(l.status))
              .map((l) => num(l.estimatedProjectValue))
              .filter((v) => v > 0)
          : [];
        const closedRevenue = closedValues.reduce((sum, v) => sum + v, 0);
        sales = {
          revenue: closedRevenue > 0 ? closedRevenue : null,
          closeRatio: totalCount > 0 ? closeRatio : null,
          avgJobSize: closedValues.length > 0 ? closedRevenue / closedValues.length : null
        };
      }

      // COST PER LEAD
      const roiCpl = leadsROI?.summary?.costPerLead;
      if (roiCpl != null && roiCpl > 0) {
        const status = getMetricStatus('costPerLead', roiCpl);
        metrics.costPerLead = {
          value: fmtCurrency(roiCpl),
          rawValue: roiCpl,
          status,
          source: 'manual',
          series: [],
          sub: 'From your Lead ROI analysis',
          cue: getCoachingCue('costPerLead', status),
          trace: makeTrace('costPerLead', {
            source: 'manual',
            sourceReason: 'From leadsROI.summary.costPerLead (your Lead ROI analysis)',
            formula: `CPL = ${fmtCurrency(roiCpl)} (as computed in your Lead ROI analysis)`,
            inputs: { costPerLead: roiCpl }
          })
        };
      } else if (campaignAgg.cpl != null && campaignAgg.cpl > 0) {
        const status = getMetricStatus('costPerLead', campaignAgg.cpl);
        metrics.costPerLead = {
          value: fmtCurrency(campaignAgg.cpl),
          rawValue: campaignAgg.cpl,
          status,
          source: 'manual',
          series: [],
          sub: `Based on ${campaignAgg.conversions} paid conversions`,
          cue: getCoachingCue('costPerLead', status),
          trace: makeTrace('costPerLead', {
            source: 'manual',
            sourceReason: 'No Lead ROI CPL — derived from your paid campaigns',
            formula: `CPL = spend ${fmtN(campaignAgg.spent)} / conversions ${fmtN(campaignAgg.conversions)} = ${fmtCurrency(campaignAgg.cpl)}`,
            inputs: { spent: campaignAgg.spent, conversions: campaignAgg.conversions, campaignCount: campaigns.length }
          })
        };
      } else {
        metrics.costPerLead = { ...EMPTY_METRIC, trace: makeTrace('costPerLead', { source: 'none', sourceReason: 'No Lead ROI analysis and no campaign conversions' }) };
      }

      // WEBSITE
      const ga4Overview = ga4Data?.overview;
      if (ga4Connected && ga4Overview) {
        const sessions = num(ga4Overview.sessions);
        const engagement = num(ga4Overview.engagementRate) * (ga4Overview.engagementRate <= 1 ? 100 : 1);
        const status = getMetricStatus('websiteEngagement', engagement);
        metrics.website = {
          value: sessions.toLocaleString(),
          rawValue: sessions,
          status,
          source: 'googleanalytics',
          series: [],
          sub: `${num(ga4Overview.activeUsers).toLocaleString()} active users · ${fmtPercent(engagement, 0)} engagement (30d)`,
          cue: getCoachingCue('websiteEngagement', status),
          trace: makeTrace('website', {
            source: 'googleanalytics',
            sourceReason: 'GA4 analytics connected for your account (last 30 days)',
            formula: `Sessions = ${fmtN(sessions)}; engagement = GA4 engagementRate ${ga4Overview.engagementRate} ${ga4Overview.engagementRate <= 1 ? '× 100 (normalized from 0–1)' : '(already 0–100)'} = ${fmtPercent(engagement, 0)}`,
            inputs: { sessions, activeUsers: num(ga4Overview.activeUsers), engagementRateRaw: ga4Overview.engagementRate, dateRange: '30daysAgo → today' }
          })
        };
      } else if (latestSeoScore != null && latestSeoScore > 0) {
        const status = getMetricStatus('seoScore', latestSeoScore);
        metrics.website = {
          value: `${Math.round(latestSeoScore)}/100`,
          rawValue: latestSeoScore,
          status,
          source: 'manual',
          series: [],
          sub: 'SEO score from your latest website audit',
          cue: getCoachingCue('seoScore', status),
          trace: makeTrace('website', {
            source: 'manual',
            sourceReason: 'GA4 not connected — showing the SEO score from your latest completed website audit',
            formula: `SEO score = ${Math.round(latestSeoScore)}/100 (latest completed audit)`,
            inputs: { seoScore: latestSeoScore }
          })
        };
      } else {
        metrics.website = { ...EMPTY_METRIC, trace: makeTrace('website', { source: 'none', sourceReason: 'GA4 not connected and no completed website audits' }) };
      }

      // SOCIAL MEDIA — card-level manual total wins over aggregated entries
      const socialOverride = num(channelTotals.socialFollowers);
      if (socialOverride > 0) {
        metrics.social = {
          value: socialOverride.toLocaleString(),
          rawValue: socialOverride,
          status: 'none',
          source: 'manual',
          series: [],
          sub: 'Total followers',
          cue: null,
          trace: makeTrace('social', {
            source: 'manual',
            sourceReason: 'Total set on the analytics card (marketingChannelTotals.socialFollowers)',
            formula: `Followers = ${fmtN(socialOverride)} (entered directly)`,
            inputs: { socialFollowers: socialOverride }
          })
        };
      } else if (socialAgg.totalFollowers > 0 || socialAgg.avgEngagement > 0) {
        metrics.social = {
          value: socialAgg.totalFollowers.toLocaleString(),
          rawValue: socialAgg.totalFollowers,
          status: socialAgg.avgEngagement > 0 ? getMetricStatus('websiteEngagement', socialAgg.avgEngagement) : 'none',
          source: 'manual',
          series: [],
          sub: socialAgg.avgEngagement > 0
            ? `Total followers · ${fmtPercent(socialAgg.avgEngagement, 1)} avg engagement`
            : 'Total followers across platforms',
          cue: null,
          trace: makeTrace('social', {
            source: 'manual',
            sourceReason: 'From your Social Media page entries (per-platform stats)',
            formula: `Followers = fb ${fmtN(socialAgg.perPlatform.facebook?.followers)} + ig ${fmtN(socialAgg.perPlatform.instagram?.followers)} + tt ${fmtN(socialAgg.perPlatform.tiktok?.followers)} + yt ${fmtN(socialAgg.perPlatform.youtube?.followers)} = ${fmtN(socialAgg.totalFollowers)}; avg engagement = mean of nonzero platform engagement = ${fmtPercent(socialAgg.avgEngagement, 1)}`,
            inputs: socialAgg.perPlatform
          })
        };
      } else {
        metrics.social = { ...EMPTY_METRIC, trace: makeTrace('social', { source: 'none', sourceReason: 'No social media stats entered' }) };
      }

      // PAID MEDIA — card-level manual total wins over campaign aggregation
      const paidOverride = num(channelTotals.paidSpend);
      if (paidOverride > 0) {
        metrics.paidMedia = {
          value: fmtCurrency(paidOverride),
          rawValue: paidOverride,
          status: 'none',
          source: 'manual',
          series: [],
          sub: 'Total ad spend',
          cue: null,
          trace: makeTrace('paidMedia', {
            source: 'manual',
            sourceReason: 'Total set on the analytics card (marketingChannelTotals.paidSpend)',
            formula: `Total spend = ${fmtCurrency(paidOverride)} (entered directly)`,
            inputs: { paidSpend: paidOverride }
          })
        };
      } else if (campaignAgg.spent > 0) {
        metrics.paidMedia = {
          value: fmtCurrency(campaignAgg.spent),
          rawValue: campaignAgg.spent,
          status: 'none',
          source: 'manual',
          series: [],
          sub: `Total spend across ${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`,
          cue: null,
          trace: makeTrace('paidMedia', {
            source: 'manual',
            sourceReason: `From ${campaigns.length} campaign docs in the campaigns collection`,
            formula: `Total spend = Σ campaign spent = ${fmtCurrency(campaignAgg.spent)} (${fmtN(campaignAgg.clicks)} clicks, ${fmtN(campaignAgg.conversions)} conversions)`,
            inputs: { spent: campaignAgg.spent, clicks: campaignAgg.clicks, revenue: campaignAgg.revenue, campaignCount: campaigns.length }
          })
        };
      } else {
        metrics.paidMedia = { ...EMPTY_METRIC, trace: makeTrace('paidMedia', { source: 'none', sourceReason: campaigns.length ? 'Campaigns tracked but no spend recorded' : 'No campaigns tracked' }) };
      }

      // NEWSLETTER — card-level manual total wins over campaign data
      const newsletterOverride = num(channelTotals.newsletterSubscribers);
      if (newsletterOverride > 0) {
        metrics.newsletter = {
          value: newsletterOverride.toLocaleString(),
          rawValue: newsletterOverride,
          status: 'none',
          source: 'manual',
          series: [],
          sub: 'Total subscribers',
          cue: null,
          trace: makeTrace('newsletter', {
            source: 'manual',
            sourceReason: 'Total set on the analytics card (marketingChannelTotals.newsletterSubscribers)',
            formula: `Subscribers = ${fmtN(newsletterOverride)} (entered directly)`,
            inputs: { newsletterSubscribers: newsletterOverride }
          })
        };
      } else if (newsletterAgg.subscribers > 0) {
        const status = newsletterAgg.openRate != null ? getMetricStatus('openRate', newsletterAgg.openRate) : 'none';
        metrics.newsletter = {
          value: newsletterAgg.subscribers.toLocaleString(),
          rawValue: newsletterAgg.subscribers,
          status,
          source: 'manual',
          series: [],
          sub: newsletterAgg.openRate != null
            ? `Total subscribers · ${fmtPercent(newsletterAgg.openRate)} avg open rate`
            : 'Total subscribers',
          cue: newsletterAgg.openRate != null ? getCoachingCue('openRate', status) : null,
          trace: makeTrace('newsletter', {
            source: 'manual',
            sourceReason: 'From your newsletter campaign entries',
            formula: `Subscribers = max(subscribers/totalEmails) over ${(newsletterRaw?.campaigns || []).length} campaigns = ${fmtN(newsletterAgg.subscribers)}; open rate = mean of campaign openRate = ${fmtPercent(newsletterAgg.openRate)}`,
            inputs: { openRate: newsletterAgg.openRate, clickRate: newsletterAgg.clickRate, subscribers: newsletterAgg.subscribers, campaignCount: (newsletterRaw?.campaigns || []).length }
          })
        };
      } else {
        metrics.newsletter = { ...EMPTY_METRIC, trace: makeTrace('newsletter', { source: 'none', sourceReason: 'No newsletter campaigns with subscriber counts' }) };
      }

      // REVIEWS
      if (reviewsData && (reviewsData.averageRating > 0 || reviewsData.totalReviews > 0)) {
        const rating = num(reviewsData.averageRating);
        const status = getMetricStatus('reviewRating', rating);
        metrics.reviews = {
          value: rating > 0 ? rating.toFixed(1) : '—',
          rawValue: rating,
          status,
          source: 'manual',
          series: [],
          sub: `${num(reviewsData.totalReviews).toLocaleString()} total reviews`,
          cue: getCoachingCue('reviewRating', status),
          trace: makeTrace('reviews', {
            source: 'manual',
            sourceReason: 'From your reviews data',
            formula: `Average rating = ${rating.toFixed(1)} across ${fmtN(reviewsData.totalReviews)} reviews`,
            inputs: { averageRating: rating, totalReviews: num(reviewsData.totalReviews) }
          })
        };
      } else {
        metrics.reviews = { ...EMPTY_METRIC, trace: makeTrace('reviews', { source: 'none', sourceReason: 'No reviews data' }) };
      }

      const debug = {
        uid,
        companyId,
        fetches,
        connections: {
          quickbooks: { connected: qbConnected, accountId: companyId },
          jobtread: {
            connected: jobtreadConnected,
            orgId: jtOrg.orgId,
            orgName: jtOrg.orgName,
            orgCount: jtOrg.orgCount,
            multipleOrgs: jtOrg.multipleOrgs,
            fieldMappings: jtMapping,
            leadsDiagnostics: jtLeadsDiagnostics
          },
          ga4: { connected: ga4Connected }
        }
      };

      if (!cancelled) {
        setState({
          companyId,
          jobtreadConnected,
          jobtreadError: jtError,
          ga4Connected,
          qbConnected,
          asOfDate: today,
          debug,
          sales,
          qualified,
          leadsActual,
          leadSettings: {
            entity: leadEntity,
            scope: leadScope,
            statusFieldId,
            statuses: leadPrefs.statuses || [],
            // Distinct status values per candidate field over the loaded
            // entity's leads ('' key = built-in status). The modal reads this
            // to populate the value checklist for the selected status field.
            statusValues,
            // Org field DEFINITIONS merged with fields OBSERVED on the fetched
            // jobs/customers — the observed set is ground truth even when the
            // definitions query is stale/empty or targetTypes don't match.
            availableCustomFields: (() => {
              const defined = jtMapping?.availableCustomFields || [];
              const definedIds = new Set(defined.map((f) => f.id));
              const observed = [
                ...((jtLeadsDiagnostics?.customFieldsSeen || []).map((f) => ({ ...f, targetType: 'job', observed: true }))),
                ...((custDiagnostics?.customFieldsSeen || []).map((f) => ({ ...f, targetType: 'account', observed: true })))
              ];
              return [...defined, ...observed.filter((f) => !definedIds.has(f.id))];
            })(),
            leadSourceField: jtMapping?.fields?.leadSource || null,
            qualifiedField: jtMapping?.fields?.qualifiedLead || null,
            mapping: jtMapping,
            orgId: jtOrg.orgId
          },
          metrics
        });
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentUser?.uid, currentUser?.email, reloadIndex, timeframe, userId, accountId]);

  return { loading, refresh, ...state };
};

export default useMarketingDashboard;
